import emojis, { Emoji, getEmojiByName } from "@/assets/emojis";
import FlipBackIcon from "@/assets/icons/flip-backward.svg";
import CustomView, {
    CustomViewInput,
} from "@/domain/entities/customViews/CustomView";
import ViewFilter from "@/domain/entities/customViews/ViewFilter";
import EmailDisplayMode from "@/domain/enums/EmailDisplayMode";
import FeatureFlag from "@/domain/enums/FeatureFlag";
import SmartLabel from "@/domain/enums/SmartLabel";
import useCreateView from "@/hooks/customView/useCreateView";
import useDeleteView from "@/hooks/customView/useDeleteView";
import useUpdateView from "@/hooks/customView/useUpdateView";
import useGetTokenProfile from "@/hooks/useGetTokenProfile";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import { customViewRootUrl, smartLabelRootUrl } from "@/router/routes";
import { useFeatureFlag } from "@cs_teamwaydev/mailvista-sdk_react";
import { TrashIcon } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DeleteAlertDialog from "../DeleteAlertDialog";
import EmojiPicker from "../EmojiPicker";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../ui/accordion";
import { Button } from "../ui/button";
import { DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RadioGroup } from "../ui/radio-group";
import Spinner from "../ui/spinner";
import DatesFilter from "./filters/DatesFilter";
import StringsFilter from "./filters/StringsFilter";
import RadioWithLabel from "./RadioGroup";
import SwitchWithLabel from "./SwitchWithLabel";

interface ViewConfigProps {
    prompt: string;
    setInputValue: (value: string) => void;
    setPrompt: (value: string) => void;
    AIAnalysis?: ViewFilter;
    view?: CustomView;
    setIsOpen: (value: boolean) => void;
}

const FormWarning = ({ children }: { children: ReactNode }) => (
    <p className="text-sm text-destructive">{children}</p>
);

interface SaveButtonProps {
    disabled: boolean;
    isLoading: boolean;
    label: string;
    onClick: () => void;
}

const SaveButton = ({
    disabled,
    isLoading,
    label,
    onClick,
}: SaveButtonProps) => (
    <Button
        className="min-w-32"
        disabled={disabled}
        onClick={onClick}
        type="button"
        variant="purpleGradient"
    >
        {isLoading ? <Spinner className="text-white" /> : label}
    </Button>
);

const ViewConfig = ({
    prompt,
    setInputValue,
    setPrompt,
    AIAnalysis,
    view,
    setIsOpen,
}: ViewConfigProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const deleteView = useDeleteView();
    const createView = useCreateView();
    const updateView = useUpdateView();

    const [isLoading, setIsLoading] = useState(false);

    const { data: userProfile, isLoading: isUserProfileLoading } =
        useGetUserProfile();
    const isCustomViewCreationEnabled = useFeatureFlag(
        FeatureFlag.CUSTOMVIEW_ENABLE_CREATION,
    );
    const { data: tokenProfile } = useGetTokenProfile();
    const isTokensExhausted = tokenProfile?.isExhausted ?? false;

    const [keywords, setKeywords] = useState<string[]>(
        view?.filters.keywords || AIAnalysis?.keywords || [],
    );
    const [identifiedSendersEmails, setIdentifiedSendersEmails] = useState<
        string[]
    >(
        view?.filters.identifiedSendersEmails ||
            AIAnalysis?.identifiedSendersEmails ||
            [],
    );
    const [identifiedRecipientsEmails, setIdentifiedRecipientsEmails] =
        useState<string[]>(
            view?.filters.identifiedRecipientsEmails ||
                AIAnalysis?.identifiedRecipientsEmails ||
                [],
        );
    const [startDate, setStartDate] = useState<Date | undefined>(
        view?.filters.startDate || AIAnalysis?.startDate,
    );
    const [endDate, setEndDate] = useState<Date | undefined>(
        view?.filters.endDate || AIAnalysis?.endDate,
    );
    const [filtersCount, setFiltersCount] = useState(0);

    const [iconName, setIconName] = useState<string>(
        view?.icon && emojis.some((emoji) => emoji.name === view.icon)
            ? view.icon
            : emojis[0].name,
    );
    const [name, setName] = useState<string>(view?.name || "");
    const [displayType, setDisplayType] = useState<EmailDisplayMode>(
        view?.displayMode || EmailDisplayMode.TABLE,
    );
    const [showUpcomingMeetings, setShowUpcomingMeetings] = useState<boolean>(
        view?.widgets.showUpcomingMeetings !== false,
    );
    const [showTodo, setShowTodo] = useState<boolean>(
        view?.widgets.showTodo !== false,
    );
    const [showDocuments, setShowDocuments] = useState<boolean>(
        view?.widgets.showDocuments !== false,
    );
    const [showLinks, setShowLinks] = useState<boolean>(
        view?.widgets.showLinks !== false,
    );
    const [showNotes, setShowNotes] = useState<boolean>(
        view?.widgets.showNotes !== false,
    );

    useEffect(() => {
        setFiltersCount(
            (keywords?.length || 0) +
                (identifiedSendersEmails?.length || 0) +
                (identifiedRecipientsEmails?.length || 0) +
                (startDate ? 1 : 0) +
                (endDate ? 1 : 0),
        );
    }, [
        keywords,
        identifiedSendersEmails,
        identifiedRecipientsEmails,
        startDate,
        endDate,
    ]);

    if (!userProfile || isUserProfileLoading) {
        return <Spinner />;
    }

    const hasNoFilters = filtersCount === 0;

    const filters = {
        keywords,
        startDate,
        endDate,
        identifiedSendersEmails,
        identifiedRecipientsEmails,
    };
    const widgets = {
        showUpcomingMeetings,
        showTodo,
        showDocuments,
        showLinks,
        showNotes,
    };

    const contactFilters = [
        {
            id: "senders",
            label: t("customViewDialog.viewConfig.senders"),
            setValues: setIdentifiedSendersEmails,
            values: identifiedSendersEmails,
        },
        {
            id: "recipients",
            label: t("customViewDialog.viewConfig.recipients"),
            setValues: setIdentifiedRecipientsEmails,
            values: identifiedRecipientsEmails,
        },
    ];

    const widgetSwitches = [
        {
            id: "upcoming-meetings",
            isChecked: showUpcomingMeetings,
            label: t("customViewDialog.viewConfig.widgets.meetings"),
            setIsChecked: setShowUpcomingMeetings,
        },
        {
            id: "todo",
            isChecked: showTodo,
            label: t("customViewDialog.viewConfig.widgets.todo"),
            setIsChecked: setShowTodo,
        },
        {
            id: "documents",
            isChecked: showDocuments,
            label: t("customViewDialog.viewConfig.widgets.documents"),
            setIsChecked: setShowDocuments,
        },
        {
            id: "links",
            isChecked: showLinks,
            label: t("customViewDialog.viewConfig.widgets.links"),
            setIsChecked: setShowLinks,
        },
        {
            id: "notes",
            isChecked: showNotes,
            label: t("customViewDialog.viewConfig.widgets.notepad"),
            setIsChecked: setShowNotes,
        },
    ];

    const canCreateView =
        !view && isCustomViewCreationEnabled && !isTokensExhausted;
    const handleCreateView = async () => {
        setIsLoading(true);
        const newView: CustomViewInput = {
            userId: userProfile.id,
            name,
            icon: iconName,
            filters,
            displayMode: displayType,
            widgets,
            userPrompt: prompt,
            noteContent: "",
            internetMessageIds: [],
        };

        const createdView = await createView(newView);
        if (!createdView) {
            setIsLoading(false);

            return;
        }
        setIsOpen(false);
        setIsLoading(false);
        setInputValue("");
        setPrompt("");
        navigate(`${customViewRootUrl}/${createdView.id}`, {
            state: { awaitingMails: true },
        });
    };

    const MAX_NAME_LENGTH = 120;

    const handleUpdateView = async () => {
        setIsLoading(true);
        if (!view) {
            return;
        }
        const updatedView = new CustomView({
            ...view,
            name,
            icon: iconName,
            filters,
            displayMode: displayType,
            widgets,
            userPrompt: prompt,
        });

        const updated = await updateView(updatedView);
        setIsOpen(false);
        setIsLoading(false);

        if (updated && view.id) {
            navigate(`${customViewRootUrl}/${view.id}`, {
                state: { awaitingMails: true },
            });
        }
    };

    const handleDeleteView = async () => {
        if (!view || !view.id) {
            return;
        }
        await deleteView(view.id);
        setIsOpen(false);
        navigate(`/${smartLabelRootUrl}/${SmartLabel.Focus}`);
    };

    return (
        <div>
            <div className="px-20 py-10">
                {!view && (
                    <div className="relative mb-4 rounded-md bg-primary p-4 text-sm text-white first-letter:uppercase">
                        {prompt}
                        <Button
                            className="absolute bottom-0 right-0 text-xs hover:bg-primary"
                            onClick={() => {
                                setInputValue(prompt);
                                setPrompt("");
                            }}
                            size="circle"
                            variant="ghost"
                        >
                            <img alt="" src={FlipBackIcon} />
                        </Button>
                    </div>
                )}
                <Accordion defaultValue="filters" type="single" collapsible>
                    <AccordionItem value="filters">
                        <AccordionTrigger>
                            {t("customViewDialog.viewConfig.filtersCount", {
                                count: filtersCount,
                            })}
                        </AccordionTrigger>
                        <AccordionContent>
                            <div className="flex flex-col gap-2 p-4">
                                <StringsFilter
                                    label={t(
                                        "customViewDialog.viewConfig.keywords",
                                    )}
                                    setValues={setKeywords}
                                    values={keywords}
                                />
                                <DatesFilter
                                    endDate={endDate}
                                    label={t(
                                        "customViewDialog.viewConfig.dates.title",
                                    )}
                                    setEndDate={setEndDate}
                                    setStartDate={setStartDate}
                                    startDate={startDate}
                                />
                                {contactFilters.map(
                                    ({ id, label, setValues, values }) => (
                                        <StringsFilter
                                            key={id}
                                            label={label}
                                            setValues={setValues}
                                            values={values}
                                            withContactSuggestions
                                        />
                                    ),
                                )}
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>

                <hr className="my-10" />

                <div className="flex flex-col gap-8">
                    <div className="flex items-end gap-2">
                        <div className="flex flex-col gap-2">
                            <EmojiPicker
                                className="h-10 w-10 p-0"
                                onEmojiSelect={(emoji: Emoji) =>
                                    setIconName(emoji.name)
                                }
                                triggerIcon={getEmojiByName(iconName)?.symbol}
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <Label>
                                {t("customViewDialog.viewConfig.name")}
                            </Label>
                            <Input
                                onChange={(e) => {
                                    if (
                                        e.target.value.length <= MAX_NAME_LENGTH
                                    ) {
                                        setName(e.target.value);
                                    }
                                }}
                                placeholder={t(
                                    "customViewDialog.viewConfig.namePlaceholder",
                                )}
                                value={name}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label>
                            {t("customViewDialog.viewConfig.displayType")}
                        </Label>
                        <div>
                            <RadioGroup
                                className="flex gap-2"
                                defaultValue={displayType}
                                onValueChange={(value) =>
                                    setDisplayType(value as EmailDisplayMode)
                                }
                            >
                                <RadioWithLabel
                                    label={t(
                                        "customViewDialog.viewConfig.table",
                                    )}
                                    value={EmailDisplayMode.TABLE}
                                />
                                <RadioWithLabel
                                    label={t(
                                        "customViewDialog.viewConfig.mosaic",
                                    )}
                                    value={EmailDisplayMode.MOSAIC}
                                />
                            </RadioGroup>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label>
                            {t("customViewDialog.viewConfig.widgets.title")}
                        </Label>
                        <div className="flex flex-col gap-2">
                            {widgetSwitches.map(
                                ({ id, isChecked, label, setIsChecked }) => (
                                    <SwitchWithLabel
                                        key={id}
                                        id={id}
                                        isChecked={isChecked}
                                        label={label}
                                        setIsChecked={setIsChecked}
                                    />
                                ),
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <DialogFooter className="border-border-primary rounded-b-lg border-t bg-surface-primary p-4">
                {view ? (
                    <div className="flex w-full items-end justify-between">
                        <DeleteAlertDialog
                            description={t(
                                "customViewDialog.deleteAlert.description",
                            )}
                            handleDeleteAction={handleDeleteView}
                            title={t("customViewDialog.deleteAlert.title")}
                            trigger={
                                <Button variant="outline">
                                    <TrashIcon className="size-4" />
                                    {t("customViewDialog.cta.delete")}
                                </Button>
                            }
                            asChild
                        />
                        <div className="flex flex-col items-end gap-2">
                            <SaveButton
                                disabled={!name || hasNoFilters}
                                isLoading={isLoading}
                                label={t("customViewDialog.cta.update")}
                                onClick={handleUpdateView}
                            />
                            {hasNoFilters && (
                                <FormWarning>
                                    {t(
                                        "customViewDialog.viewConfig.noFilterConfigured",
                                    )}
                                </FormWarning>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex w-full flex-col items-end gap-2">
                        <SaveButton
                            disabled={!name || !canCreateView || hasNoFilters}
                            isLoading={isLoading}
                            label={t("customViewDialog.cta.create")}
                            onClick={handleCreateView}
                        />
                        {!canCreateView && (
                            <FormWarning>
                                {isTokensExhausted
                                    ? t(
                                          "customViewDialog.viewConfig.tokensExhausted",
                                      )
                                    : t(
                                          "customViewDialog.viewConfig.maxViewsReached",
                                      )}
                            </FormWarning>
                        )}
                        {canCreateView && hasNoFilters && (
                            <FormWarning>
                                {t(
                                    "customViewDialog.viewConfig.noFilterConfigured",
                                )}
                            </FormWarning>
                        )}
                    </div>
                )}
            </DialogFooter>
        </div>
    );
};

export default ViewConfig;
